import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Autocomplete, TextField, Box, useTheme } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { DistributionManager } from '../../../APIManager'
import { type IDistributionSeries } from '../../../models/DistributionSeries'
import GenericButton from '../../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../../components/GenericButton/styles'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useAppSearchParams } from '../../../hooks/useAppSearchParams'
import { useAppLocalStorage } from '../../../hooks/useAppLocalStorage'

interface ITerm {
  termId: string
  termName: string
}

interface ProjectAndTermHeaderProps {
  onApply?: (project: IDistributionSeries | null, term: ITerm | null) => void
  disabledProjectAndTermHeader?: boolean
}

const LOCAL_STORAGE_PROJECT_KEY = 'reportsSelectedProjectId'
const LOCAL_STORAGE_TERM_KEY = 'reportsSelectedTermId'

export const ProjectAndTermHeader: React.FC<ProjectAndTermHeaderProps> = ({ onApply, disabledProjectAndTermHeader }) => {
  const { searchParams, setUrlParams } = useAppSearchParams()
  const { setLocalStorage } = useAppLocalStorage()
  const [selectedProject, setSelectedProject] = useState<IDistributionSeries | null>(null)
  const [selectedTerm, setSelectedTerm] = useState<ITerm | null>(null)
  const theme = useTheme()

  const autocompleteSx = useMemo(() => ({
    [theme.breakpoints.up('md')]: { maxWidth: '256px' }
  }), [theme])

  const urlProjectId = searchParams.get('projectId')
  const urlTermId = searchParams.get('termId')

  const {
    data: reportsProjects = [],
    isLoading: projectsLoading
  } = useQuery({
    queryKey: ['ReportsProjects'],
    queryFn: async () => (await DistributionManager.getAllDistributionSeries({ activeTerms: true })).data,
    retry: false,
    enabled: true
  })

  const defaultProject = useMemo(() => {
    if (urlProjectId && reportsProjects.length) {
      return reportsProjects.find((p: IDistributionSeries) => p.distributionSeriesId === urlProjectId) || null
    }
    const storedId = localStorage.getItem(LOCAL_STORAGE_PROJECT_KEY)
    if (storedId && reportsProjects.some((p: IDistributionSeries) => p.distributionSeriesId === storedId)) {
      return reportsProjects.find((p: IDistributionSeries) => p.distributionSeriesId === storedId) || null
    }
    return null
  }, [urlProjectId, reportsProjects])

  const defaultTerm = useMemo(() => {
    if (urlTermId && defaultProject?.terms?.length) {
      return defaultProject.terms.find((t: ITerm) => t.termId === urlTermId) || null
    }
    const storedTermId = localStorage.getItem(LOCAL_STORAGE_TERM_KEY)
    if (storedTermId && defaultProject?.terms?.some((t: ITerm) => t.termId === storedTermId)) {
      return defaultProject.terms.find((t: ITerm) => t.termId === storedTermId) || null
    }
    return null
  }, [urlTermId, defaultProject])

  useEffect(() => {
    setSelectedProject(defaultProject)
    setSelectedTerm(defaultTerm)
  }, [defaultProject, defaultTerm])

  useEffect(() => {
    if (!urlProjectId && !urlTermId) {
      const storedProjectId = localStorage.getItem(LOCAL_STORAGE_PROJECT_KEY)
      const storedTermId = localStorage.getItem(LOCAL_STORAGE_TERM_KEY)
      if (storedProjectId && storedTermId) {
        setUrlParams({
          projectId: storedProjectId || undefined,
          termId: storedTermId || undefined
        })
      }
    }
  }, [])

  const handleProjectChange = useCallback((_: any, newValue: IDistributionSeries | null) => {
    setSelectedProject(newValue)
    setSelectedTerm(null)
  }, [])

  const handleTermChange = useCallback((_: any, newValue: ITerm | null) => {
    setSelectedTerm(newValue)
  }, [])

  const handleApply = useCallback(() => {
    setUrlParams({
      projectId: selectedProject?.distributionSeriesId,
      termId: selectedTerm?.termId
    })
    setLocalStorage({
      [LOCAL_STORAGE_PROJECT_KEY]: selectedProject?.distributionSeriesId,
      [LOCAL_STORAGE_TERM_KEY]: selectedTerm?.termId
    })
    if (onApply) onApply(selectedProject, selectedTerm)
  }, [onApply, selectedProject, selectedTerm, setUrlParams, setLocalStorage])

  const autocompleteProps = {
    autoHighlight: true,
    disableClearable: true,
    size: 'small' as const
  }

  return (
    <Box display="flex" alignItems="center" gap={2}>
      <Autocomplete
        options={reportsProjects}
        value={selectedProject}
        onChange={handleProjectChange}
        getOptionLabel={(option: IDistributionSeries) => option.title}
        isOptionEqualToValue={(option: IDistributionSeries, value: IDistributionSeries) => option.distributionSeriesId === value.distributionSeriesId}
        loading={projectsLoading}
        disabled={disabledProjectAndTermHeader}
        {...autocompleteProps}
        renderInput={(params) => (
          <TextField {...params} label="Select Project" variant="outlined" />
        )}
        sx={{
          ...autocompleteSx
        }}
        fullWidth
      />
      <Autocomplete
        options={selectedProject?.terms || []}
        value={selectedTerm}
        onChange={handleTermChange}
        getOptionLabel={(option: ITerm) => option.termName}
        isOptionEqualToValue={(option: ITerm, value: ITerm) => option.termId === value.termId}
        loading={projectsLoading}
        disabled={disabledProjectAndTermHeader}
        {...autocompleteProps}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Term"
            variant="outlined"
          />
        )}
        sx={{ ...autocompleteSx }}
        fullWidth
      />
      <GenericButton
        buttonType={ButtonType.Primary}
        buttonSize={ButtonSize.Medium}
        icon={<ArrowForwardIcon />}
        onClick={handleApply}
      />
    </Box>
  )
}
